import {
  CheckCircle2,
  Download,
  FileText,
  Folder,
  Loader2,
  Paperclip,
  Pencil,
  Plus,
  X
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '~/components/common/Button'
import { InputArea } from '~/components/common/InputArea'
import { CloseButton, SplitPaneViewer } from '~/components/common/SplitPaneViewer'
import { Typography } from '~/components/common/Typography'
import { IconCurieStar } from '~/components/icons'
import DocMarkdown from '~/components/documents/DocMarkdown'
import { useTaskContext } from '~/contexts/TaskContext'
import {
  ALLOWED_CONTEXT_UPLOAD_EXTENSIONS,
  isAllowedFileExtension,
  useContextUpload
} from '~/hooks/useContextUpload'
import {
  AgentTaskStatus,
  FileUpload,
  MigrationDocumentOutput,
  MigrationRiskReviewFeedback,
  TaskStatusAndRequestResponseTuple
} from '~/protos/agents'
import { getContextUploadDownloadUrl } from '~/services/organizationService'
import { submitRiskReview } from '~/services/taskService'
import type { ContextUploadItem } from '~/types/contextUpload'
import { cm } from '~/utils/utils'
import { BetterToast } from '~/utils/toast'

/**
 * Whole-review attachment policy. Mirrors the agent-side validation in
 * `MigrationAcceleratorAgent._validate_feedback_attachments` so the FE
 * rejects obviously bad files (wrong extension, too large, too many)
 * before bouncing through S3. The agent re-validates regardless — the
 * FE check is purely for UX.
 */
const RISK_FEEDBACK_MAX_FILE_BYTES = 5 * 1024 * 1024
const RISK_FEEDBACK_MAX_FILES_TOTAL = 5

/**
 * Migration risk-review modal — Figma `workflow-automation` node 81:31275
 * (browser layout) + node 81:31498 (Chat Box).
 *
 * Flow per the user spec:
 *   - The user reviews ALL risk sections (one whole section per endpoint)
 *     using the sidebar + prev/next chevrons. There is NO per-risk parsing
 *     or per-risk feedback editor — feedback applies to the whole review.
 *   - A single ChatBox docked at the bottom collects ONE consolidated
 *     feedback (free-text + optional supporting files). When the user
 *     clicks "Regenerate Docs", that single feedback is submitted to the
 *     agent which then regenerates every endpoint's risk section based
 *     on it (the same feedback is passed to each endpoint's regen call).
 *   - "Acknowledge & Skip" in the bottom review hub is the explicit way
 *     to consume the single feedback round without typing anything.
 *
 * In read-only mode (status is MIGRATION_RISKS_REVIEWED, or the agent
 * already consumed the feedback round), the ChatBox is replaced by a
 * read-only "Your feedback" panel showing what was submitted.
 */
export interface MigrationRiskSectionEditorProps {
  tuples: TaskStatusAndRequestResponseTuple[]
  /** Index of the tuple this editor was opened from. */
  index: number
  taskId: number
  workspaceUuid: string
  uuid: string
  /** Current task status (top-level). Drives canSubmitFeedback. */
  currentTaskStatus: AgentTaskStatus | null
  onClose: () => void
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface EndpointRiskGroup {
  endpointKey: string
  rawMd: string
}

/** Cross-endpoint flat reference used by the prev/next chevrons. */
interface FlatEndpointRef {
  endpointKey: string
  /** Index in the flattened list. */
  flatIndex: number
}

// ─── Markdown helpers ───────────────────────────────────────────────────────

/**
 * Walk the tuple list to find the closest tuple carrying a non-empty
 * `migrationDocOutput`.
 *
 * Direction policy:
 *   - When the click landed on a `MIGRATION_RISKS_REVIEWED` row, the user is
 *     looking at the feedback they submitted. The risks they actually reviewed
 *     belong to the v1 doc that was generated *before* this row, so prefer
 *     walking BACKWARD first (the v2 doc on the next row would show the
 *     agent's regenerated risks, which the submitted feedback doesn't map to).
 *   - In every other case, walk forward first so a v2 doc — if any — wins
 *     over the older v1 doc.
 */
function findMigrationDocOutput(
  tuples: TaskStatusAndRequestResponseTuple[],
  startIndex: number
): MigrationDocumentOutput | null {
  const isUsable = (out: MigrationDocumentOutput | undefined): out is MigrationDocumentOutput => {
    if (!out) return false
    const hasMap = Object.keys(out.migDocsRiskMap || {}).length > 0
    const hasDocs = Object.keys(out.migDocsMap || {}).length > 0
    return hasMap || hasDocs
  }
  const here = tuples[startIndex]
  const preferBackward = here?.taskStatus === AgentTaskStatus.MIGRATION_RISKS_REVIEWED
  const fwd = () => {
    for (let i = startIndex; i < tuples.length; i += 1) {
      const out = tuples[i]?.response?.[0]?.responseContentStream?.migrationDocOutput
      if (isUsable(out)) return out!
    }
    return null
  }
  const bwd = () => {
    for (let i = startIndex - 1; i >= 0; i -= 1) {
      const out = tuples[i]?.response?.[0]?.responseContentStream?.migrationDocOutput
      if (isUsable(out)) return out!
    }
    return null
  }
  return preferBackward ? (bwd() ?? fwd()) : (fwd() ?? bwd())
}

/**
 * Walks the tuple list newest-first and returns the most recently submitted
 * whole-review feedback (text + uploads), if any. Returns null when the
 * user hasn't submitted yet OR when they submitted an empty round
 * ("Acknowledge & Skip") — the read-only panel suppresses itself in
 * that case rather than showing an empty box.
 */
function collectSubmittedReviewFeedback(
  tuples: TaskStatusAndRequestResponseTuple[]
): MigrationRiskReviewFeedback | null {
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const tuple = tuples[i]
    if (tuple.taskStatus !== AgentTaskStatus.MIGRATION_RISKS_REVIEWED) continue
    const fb = tuple.request?.migrationRiskReviewFeedback
    if (!fb) continue
    const hasText = (fb.feedback || '').trim().length > 0
    const hasFiles = (fb.uploadedFiles || []).length > 0
    if (hasText || hasFiles) return fb
  }
  return null
}

/**
 * Markdown renderer using the shared DocMarkdown component so inline
 * code spans, links, lists, and bold text behave like the rest of the app.
 */
function RiskFieldMarkdown({ md }: { md?: string }) {
  if (!md) return null
  const formattedMarkdown = md
    .replace(
      /\s+(?=\*\*(?:Severity|Type|Description|Why It Matters For Migration|Evidence \/ Missing Evidence|Recommended Validation Before Migration):\*\*)/g,
      '\n\n'
    )
    .replace(
      /\s+(?=(?:Severity|Type|Description|Why It Matters For Migration|Evidence \/ Missing Evidence|Recommended Validation Before Migration):)/g,
      '\n\n'
    )
    .replace(/\s+(?=\*\*Citations\*\*)/g, '\n\n')
    .replace(/\s+(?=Risk \/ Assumption\s+\d+)/g, '\n\n')
  return (
    <div className='prose prose-sm max-w-none text-tokens-neutral-text-primary'>
      <DocMarkdown
        text={formattedMarkdown}
        fontSize='sm'
        className='max-w-none !pl-0 text-[14px] leading-[22px] text-tokens-neutral-text-secondary [&_code]:rounded-md [&_code]:bg-tokens-neutral-fill-fill-6 [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-geistMono [&_code]:text-[12px] [&_h1]:mb-4 [&_h1]:text-[14px] [&_h1]:font-bold [&_h2]:mb-4 [&_h2]:text-[14px] [&_h2]:font-bold [&_h3]:mb-4 [&_h3]:text-[14px] [&_h3]:font-bold [&_li]:my-1 [&_p]:mb-5 [&_strong]:font-bold [&_strong]:text-tokens-neutral-text-primary'
      />
    </div>
  )
}

// ─── ChatBox (Figma node 81:31498) ──────────────────────────────────────────

interface ChatBoxProps {
  feedback: string
  onFeedbackChange: (v: string) => void
  attachments: ContextUploadItem[]
  hasPendingUploads: boolean
  attachmentError: string | null
  onAttachFiles: (files: FileList) => void
  onAttachFolder: () => void
  onRemoveAttachment: (uploadId: number) => void
  onCancel: () => void
  onSubmit: () => void
  isSubmitting: boolean
}

/**
 * Single whole-review feedback chatbox. Matches Figma node 81:31498 — a
 * rounded card with the textarea on top, an "Attach" `+` button bottom-left,
 * and "Cancel" / "Regenerate Docs" actions bottom-right. The submit button
 * is disabled until there is at least some text or one completed attachment.
 */
function ChatBox({
  feedback,
  onFeedbackChange,
  attachments,
  hasPendingUploads,
  attachmentError,
  onAttachFiles,
  onAttachFolder,
  onRemoveAttachment,
  onCancel,
  onSubmit,
  isSubmitting
}: ChatBoxProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false)
  const acceptedExtensions = useMemo(
    () => Array.from(ALLOWED_CONTEXT_UPLOAD_EXTENSIONS).join(','),
    []
  )

  const handleAttachFilesClick = () => {
    setIsAttachMenuOpen(false)
    fileInputRef.current?.click()
  }
  const handleAttachFolderClick = () => {
    setIsAttachMenuOpen(false)
    onAttachFolder()
  }
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) onAttachFiles(e.target.files)
    e.target.value = ''
  }

  const hasAnyCompletedAttachment = attachments.some(a => a.uploadStatus === 'completed')
  const hasInput = Boolean(feedback.trim()) || hasAnyCompletedAttachment
  const isSubmitDisabled =
    !hasInput || hasPendingUploads || Boolean(attachmentError) || isSubmitting

  return (
    <div className='pointer-events-none absolute inset-x-8 bottom-12 z-20 flex justify-center'>
      <div
        className='pointer-events-auto w-full max-w-[800px] rounded-[24px] border border-tokens-status-link-1 bg-tokens-neutral-bg-white shadow-popover'
        data-testid='risk-review-chatbox'
      >
        <div className='px-4 pt-3'>
          <InputArea
            value={feedback}
            onChange={e => onFeedbackChange(e.target.value)}
            placeholder='Describe the changes you want to see in the regenerated risks…'
            fullWidth
            size='default'
            className='min-h-[64px] border-0 bg-transparent px-0 py-1 text-base leading-6 placeholder:text-base placeholder:leading-6 !shadow-none focus:border-0 focus:!shadow-none'
          />
        </div>

        {attachments.length > 0 ? (
          <ul className='mx-4 mt-1 space-y-1' data-testid='risk-review-chatbox-attachments'>
            {attachments.map(u => (
              <li
                key={u.id}
                className='flex items-center justify-between gap-2 rounded-md border border-tokens-neutral-border-2 bg-tokens-neutral-fill-fill-7 px-2 py-1'
              >
                <div className='flex min-w-0 items-center gap-2'>
                  {u.uploadStatus === 'uploading' || u.uploadStatus === 'pending' ? (
                    <Loader2 className='h-3.5 w-3.5 flex-shrink-0 animate-spin text-tokens-neutral-text-tertiary' />
                  ) : u.uploadStatus === 'error' ? (
                    <X className='h-3.5 w-3.5 flex-shrink-0 text-tokens-status-error-1' />
                  ) : u.type === 'directory' ? (
                    <Folder className='h-3.5 w-3.5 flex-shrink-0 text-tokens-neutral-text-tertiary' />
                  ) : (
                    <Paperclip className='h-3.5 w-3.5 flex-shrink-0 text-tokens-neutral-text-tertiary' />
                  )}
                  <Typography
                    fontFamily='geistMono'
                    fontSize='bodySm'
                    className={cm(
                      'truncate',
                      u.uploadStatus === 'error'
                        ? 'text-tokens-status-error-1'
                        : 'text-tokens-neutral-text-primary'
                    )}
                  >
                    {u.name}
                  </Typography>
                  <Typography
                    fontFamily='geist'
                    fontSize='labelSm'
                    className='flex-shrink-0 text-tokens-neutral-text-tertiary'
                  >
                    {u.uploadStatus === 'uploading'
                      ? `${u.uploadProgress ?? 0}%`
                      : u.uploadStatus === 'error'
                        ? (u.error ?? 'failed')
                        : u.size}
                  </Typography>
                </div>
                <button
                  type='button'
                  aria-label={`Remove ${u.name}`}
                  onClick={() => onRemoveAttachment(u.id)}
                  className='inline-flex h-5 w-5 items-center justify-center rounded text-tokens-neutral-text-tertiary hover:bg-tokens-neutral-fill-fill-6'
                >
                  <X className='h-3.5 w-3.5' />
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {attachmentError ? (
          <Typography
            fontFamily='geist'
            fontSize='labelSm'
            className='mx-4 mt-2 text-tokens-status-error-1'
          >
            {attachmentError}
          </Typography>
        ) : null}

        <div className='flex items-center justify-between gap-2 px-4 py-3'>
          <div className='relative'>
            <button
              type='button'
              onClick={() => setIsAttachMenuOpen(open => !open)}
              disabled={attachments.length >= RISK_FEEDBACK_MAX_FILES_TOTAL}
              aria-label='Attach files or folder'
              className={cm(
                'inline-flex h-8 w-8 items-center justify-center rounded-xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white text-tokens-neutral-text-primary transition-colors',
                attachments.length >= RISK_FEEDBACK_MAX_FILES_TOTAL
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:bg-tokens-neutral-fill-fill-6'
              )}
              data-testid='risk-review-chatbox-attach-button'
            >
              <Plus className='h-4 w-4' />
            </button>
            {isAttachMenuOpen ? (
              <div className='absolute bottom-10 left-0 z-10 w-44 overflow-hidden rounded-xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white p-1 shadow-heavy'>
                <button
                  type='button'
                  onClick={handleAttachFilesClick}
                  className='flex w-full select-none items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-tokens-neutral-text-primary hover:bg-tokens-neutral-fill-fill-6'
                >
                  <FileText className='h-4 w-4 text-tokens-neutral-text-tertiary' />
                  Upload files
                </button>
                <button
                  type='button'
                  onClick={handleAttachFolderClick}
                  className='flex w-full select-none items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-tokens-neutral-text-primary hover:bg-tokens-neutral-fill-fill-6'
                >
                  <Folder className='h-4 w-4 text-tokens-neutral-text-tertiary' />
                  Upload folder
                </button>
              </div>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type='file'
            multiple
            className='hidden'
            onChange={handleFileInputChange}
            accept={acceptedExtensions}
            data-testid='risk-review-chatbox-attach-input'
          />
          <div className='flex items-center gap-2'>
            <Button variant='borderless' onClick={onCancel} isDisabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              isDisabled={isSubmitDisabled}
              data-testid='risk-review-chatbox-submit-button'
            >
              {isSubmitting ? 'Submitting…' : hasPendingUploads ? 'Uploading…' : 'Regenerate Docs'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface ReviewChoiceHubProps {
  onAcknowledge: () => void
  onProvideFeedback: () => void
  isSubmitting: boolean
}

function ReviewChoiceHub({ onAcknowledge, onProvideFeedback, isSubmitting }: ReviewChoiceHubProps) {
  return (
    <div className='pointer-events-none absolute inset-x-8 bottom-0 z-20 flex h-[278px] justify-center bg-gradient-to-b from-white/0 to-tokens-neutral-bg-white py-12'>
      <div className='pointer-events-auto relative h-[182px] w-full max-w-[800px]'>
        <div className='absolute left-1/2 top-0 flex h-[52px] w-[calc(100%-15px)] -translate-x-1/2 flex-col overflow-hidden rounded-t-[24px] border-x border-t border-tokens-neutral-border-colored/20 bg-tokens-neutral-bg-grey-1'>
          <div className='flex h-[36px] items-center px-6'>
            <div className='inline-flex select-none items-center gap-2 rounded-[12px] py-2 text-[14px] font-medium leading-[22px] text-tokens-status-link-3'>
              <IconCurieStar className='h-4 w-4' />
              Review Risk/Assumption
            </div>
          </div>
          <div className='h-px w-full bg-tokens-neutral-border-2' />
        </div>

        <div
          className='absolute left-1/2 top-9 flex w-full -translate-x-1/2 flex-col gap-[10px] rounded-[24px] border border-tokens-status-link-1 bg-tokens-neutral-bg-grey-2 p-4 shadow-popover'
          data-testid='risk-review-choice-hub'
        >
          <button
            type='button'
            onClick={onAcknowledge}
            disabled={isSubmitting}
            className='flex h-[52px] w-full select-none items-center gap-3 rounded-[16px] bg-tokens-neutral-bg-white px-6 text-left text-[14px] font-bold leading-[18px] text-tokens-neutral-text-primary transition-shadow hover:shadow-button disabled:cursor-not-allowed disabled:opacity-50'
          >
            <CheckCircle2 className='h-4 w-4 shrink-0 text-tokens-neutral-icon-hover' />
            {isSubmitting ? 'Submitting...' : 'Acknowledge & Skip'}
          </button>
          <button
            type='button'
            onClick={onProvideFeedback}
            disabled={isSubmitting}
            className='flex h-[52px] w-full select-none items-center gap-3 rounded-[16px] bg-tokens-neutral-bg-white px-6 text-left text-[14px] font-bold leading-[18px] text-tokens-neutral-text-secondary transition-shadow hover:shadow-button disabled:cursor-not-allowed disabled:opacity-50'
          >
            <Pencil className='h-4 w-4 shrink-0 text-tokens-neutral-icon-hover' />
            Provide feedback
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Read-only feedback panel (post-submission) ─────────────────────────────

interface ReadOnlyFeedbackPanelProps {
  feedback: string
  attachments: FileUpload[]
  workspaceUuid: string
}

interface ReadOnlyAttachmentRowProps {
  file: FileUpload
  workspaceUuid: string
}

/**
 * Single attachment row in the read-only feedback panel. Clicking the row
 * fetches a presigned download URL from the same context-upload endpoint
 * the upload pipeline used and triggers a browser download. We deliberately
 * avoid baking the S3 path or any short-lived URL into the rendered DOM —
 * the URL is only minted on click so it stays fresh and never leaks past
 * the user's intent. Mirrors the click-to-download behaviour in
 * `MessageUploadCard` so the two surfaces stay consistent.
 */
function ReadOnlyAttachmentRow({ file, workspaceUuid }: ReadOnlyAttachmentRowProps) {
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = async () => {
    if (isDownloading) return
    if (!workspaceUuid || !file.fileS3Path) {
      BetterToast.error('Cannot download — missing workspace or file path.')
      return
    }
    setIsDownloading(true)
    try {
      const { url, filename } = await getContextUploadDownloadUrl(workspaceUuid, file.fileS3Path)
      const a = document.createElement('a')
      a.href = url
      a.download = filename || file.fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (error) {
      BetterToast.error(error instanceof Error ? error.message : 'Failed to download file')
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <button
      type='button'
      onClick={handleDownload}
      disabled={isDownloading}
      title={`Download ${file.fileName}`}
      data-testid='risk-review-readonly-attachment'
      className={cm(
        'group inline-flex max-w-full items-center gap-2 rounded-full',
        'border border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1',
        'pl-2.5 pr-1 py-1 text-tokens-neutral-text-primary transition-all',
        'hover:border-tokens-primary-3 hover:bg-tokens-neutral-bg-white hover:shadow-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tokens-primary-3',
        isDownloading && 'cursor-wait opacity-70'
      )}
    >
      <Paperclip className='h-3.5 w-3.5 flex-shrink-0 text-tokens-neutral-text-tertiary' />
      <Typography fontFamily='geistMono' fontSize='bodySm' className='truncate'>
        {file.fileName}
      </Typography>
      <span
        className={cm(
          'ml-1 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full',
          'bg-tokens-neutral-fill-fill-6 text-tokens-neutral-text-secondary',
          'transition-colors group-hover:bg-tokens-primary-3 group-hover:text-tokens-neutral-bg-white'
        )}
      >
        {isDownloading ? (
          <Loader2 className='h-3 w-3 animate-spin' />
        ) : (
          <Download className='h-3 w-3' />
        )}
      </span>
    </button>
  )
}

function ReadOnlyFeedbackPanel({
  feedback,
  attachments,
  workspaceUuid
}: ReadOnlyFeedbackPanelProps) {
  return (
    <div className='pointer-events-none absolute inset-x-8 bottom-10 z-20 flex justify-center'>
      <div className='pointer-events-auto relative min-h-[132px] w-full max-w-[800px]'>
        <div className='absolute left-1/2 top-0 flex h-[52px] w-[calc(100%-15px)] -translate-x-1/2 flex-col overflow-hidden rounded-t-[24px] border-x border-t border-tokens-neutral-border-colored/20 bg-tokens-neutral-bg-grey-1'>
          <div className='flex h-[36px] items-center px-6'>
            <div className='inline-flex select-none items-center gap-2 rounded-[12px] py-2 text-[14px] font-medium leading-[22px] text-tokens-status-link-3'>
              <IconCurieStar className='h-4 w-4' />
              Review Risk/Assumption
            </div>
          </div>
          <div className='h-px w-full bg-tokens-neutral-border-2' />
        </div>

        <div
          className='absolute left-1/2 top-9 w-full -translate-x-1/2 rounded-[24px] border border-tokens-status-link-1 bg-tokens-neutral-bg-grey-2 p-4 shadow-popover'
          data-testid='risk-review-readonly-panel'
        >
          <div className='rounded-[16px] bg-tokens-neutral-bg-white px-6 py-4'>
            <Typography
              fontFamily='geist'
              fontSize='labelSm'
              fontWeight='semibold'
              className='uppercase tracking-[0.08em] text-tokens-neutral-text-tertiary'
            >
              Your feedback
            </Typography>
            {feedback ? (
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='mt-1.5 whitespace-pre-wrap text-tokens-neutral-text-primary'
              >
                {feedback}
              </Typography>
            ) : (
              <Typography
                fontFamily='geist'
                fontSize='bodySm'
                className='mt-1.5 italic text-tokens-neutral-text-tertiary'
              >
                No feedback text — see attachments below.
              </Typography>
            )}
            {attachments.length > 0 ? (
              <div
                className='mt-3 flex flex-wrap gap-2'
                data-testid='risk-review-readonly-attachments'
              >
                {attachments.map(f => (
                  <ReadOnlyAttachmentRow
                    key={`${f.fileS3Path}-${f.fileName}`}
                    file={f}
                    workspaceUuid={workspaceUuid}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main component ─────────────────────────────────────────────────────────

export function MigrationRiskSectionEditor({
  tuples,
  index,
  taskId,
  workspaceUuid,
  uuid,
  currentTaskStatus,
  onClose
}: MigrationRiskSectionEditorProps) {
  const { refetch: refetchTask } = useTaskContext()

  useEffect(() => {
    const body = document.body
    const html = document.documentElement
    const previousBodyOverflow = body.style.overflow
    const previousHtmlOverflow = html.style.overflow
    const previousBodyPaddingRight = body.style.paddingRight
    const scrollbarWidth = window.innerWidth - html.clientWidth

    body.style.overflow = 'hidden'
    html.style.overflow = 'hidden'
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`
    }

    return () => {
      body.style.overflow = previousBodyOverflow
      html.style.overflow = previousHtmlOverflow
      body.style.paddingRight = previousBodyPaddingRight
    }
  }, [])

  const migrationDocOutput = useMemo(() => findMigrationDocOutput(tuples, index), [tuples, index])

  const groups: EndpointRiskGroup[] = useMemo(() => {
    if (!migrationDocOutput) return []
    const map = migrationDocOutput.migDocsRiskMap || {}
    return Object.entries(map)
      .filter(([, md]) => (md || '').trim())
      .map(([endpointKey, md]) => ({
        endpointKey,
        rawMd: md
      }))
  }, [migrationDocOutput])

  const flatEndpoints: FlatEndpointRef[] = useMemo(() => {
    return groups.map((g, index) => ({ endpointKey: g.endpointKey, flatIndex: index }))
  }, [groups])

  const canSubmitFeedback = currentTaskStatus === AgentTaskStatus.MIGRATION_AWAITING_RISK_REVIEW

  const submittedReviewFeedback = useMemo(() => collectSubmittedReviewFeedback(tuples), [tuples])

  // ── Navigation state ──────────────────────────────────────────────────
  const [activeEndpointKey, setActiveEndpointKey] = useState<string>(
    flatEndpoints[0]?.endpointKey ?? ''
  )

  useEffect(() => {
    if (flatEndpoints.length === 0) {
      setActiveEndpointKey('')
      return
    }
    if (!flatEndpoints.some(f => f.endpointKey === activeEndpointKey)) {
      setActiveEndpointKey(flatEndpoints[0].endpointKey)
    }
  }, [flatEndpoints, activeEndpointKey])

  const activeEndpointRef =
    flatEndpoints.find(f => f.endpointKey === activeEndpointKey) ?? flatEndpoints[0]
  const activeGroup = groups.find(g => g.endpointKey === activeEndpointRef?.endpointKey)
  const activeFlatIndex = activeEndpointRef?.flatIndex ?? 0

  // ── Whole-review feedback state (single chatbox) ──────────────────────
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)
  const [feedbackText, setFeedbackText] = useState('')
  const [attachmentIds, setAttachmentIds] = useState<number[]>([])
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)

  const { contextUploads, handleFilesUpload, handleFolderUpload, handleRemoveContextUpload } =
    useContextUpload(workspaceUuid, {
      onUploadError: (upload, err) => {
        console.error('Risk feedback attachment upload failed:', upload.name, err)
      }
    })

  const uploadById = useMemo(() => {
    const m: Record<number, ContextUploadItem> = {}
    for (const u of contextUploads) m[u.id] = u
    return m
  }, [contextUploads])

  useEffect(() => {
    setAttachmentIds(prev => {
      const existing = new Set(prev)
      const newIds = contextUploads.map(u => u.id).filter(id => !existing.has(id))
      return newIds.length > 0 ? [...prev, ...newIds] : prev
    })
  }, [contextUploads])

  const attachments: ContextUploadItem[] = useMemo(
    () => attachmentIds.map(id => uploadById[id]).filter((u): u is ContextUploadItem => Boolean(u)),
    [attachmentIds, uploadById]
  )

  const hasPendingUploads = useMemo(
    () => attachments.some(u => u.uploadStatus === 'pending' || u.uploadStatus === 'uploading'),
    [attachments]
  )

  /**
   * Validates a candidate file against the whole-review attachment policy
   * (extension, size cap, total file count). Returns a human-facing error
   * message if rejected, or null if the file is acceptable.
   */
  const rejectionReason = useCallback((file: File, currentCount: number): string | null => {
    if (currentCount >= RISK_FEEDBACK_MAX_FILES_TOTAL) {
      return `Limit reached: max ${RISK_FEEDBACK_MAX_FILES_TOTAL} attachments per submission.`
    }
    if (!isAllowedFileExtension(file.name)) {
      return `Unsupported file type: ${file.name}`
    }
    if (file.size > RISK_FEEDBACK_MAX_FILE_BYTES) {
      return `File too large (max ${RISK_FEEDBACK_MAX_FILE_BYTES / (1024 * 1024)}MB): ${file.name}`
    }
    return null
  }, [])

  const attachFiles = useCallback(
    (files: FileList) => {
      if (!workspaceUuid) {
        setAttachmentError('Workspace not selected — cannot attach files.')
        return
      }
      const validFiles: File[] = []
      const currentCount = attachments.length
      let firstError: string | null = null
      for (const file of Array.from(files)) {
        const reason = rejectionReason(file, currentCount + validFiles.length)
        if (reason) {
          if (!firstError) firstError = reason
          continue
        }
        validFiles.push(file)
      }
      setAttachmentError(firstError)
      if (validFiles.length === 0) return
      const dt = new DataTransfer()
      validFiles.forEach(f => dt.items.add(f))
      handleFilesUpload(dt.files).catch(error => {
        console.error('Risk feedback file upload failed:', error)
        setAttachmentError('Failed to upload files. Please try again.')
      })
    },
    [attachments.length, handleFilesUpload, rejectionReason, workspaceUuid]
  )

  const attachFolder = useCallback(() => {
    if (!workspaceUuid) {
      setAttachmentError('Workspace not selected — cannot attach folders.')
      return
    }
    if (attachments.length >= RISK_FEEDBACK_MAX_FILES_TOTAL) {
      setAttachmentError(
        `Limit reached: max ${RISK_FEEDBACK_MAX_FILES_TOTAL} attachments per submission.`
      )
      return
    }
    setAttachmentError(null)
    handleFolderUpload().catch(error => {
      console.error('Risk feedback folder upload failed:', error)
      setAttachmentError('Failed to upload folder. Please try again.')
    })
  }, [attachments.length, handleFolderUpload, workspaceUuid])

  const removeAttachment = useCallback(
    (uploadId: number) => {
      setAttachmentIds(prev => prev.filter(id => id !== uploadId))
      handleRemoveContextUpload(uploadId)
    },
    [handleRemoveContextUpload]
  )

  const buildPayload = (): MigrationRiskReviewFeedback => {
    // Drop any non-completed uploads — pending / failed entries cannot be
    // sent to the agent (no s3 path). The submit button is gated on
    // hasPendingUploads anyway, this is belt-and-suspenders.
    const uploadedFiles: FileUpload[] = attachmentIds
      .map(id => uploadById[id])
      .filter(
        (u): u is ContextUploadItem =>
          Boolean(u) && u.uploadStatus === 'completed' && Boolean(u.s3Path)
      )
      .map(u => ({
        fileName: u.type === 'directory' ? `${u.name}.tar.gz` : u.name,
        fileS3Path: u.s3Path!
      }))
    return {
      feedback: feedbackText.trim(),
      uploadedFiles
    }
  }

  const submit = useCallback(
    async (withFeedback: boolean) => {
      if (isSubmittingRef.current) return
      if (!canSubmitFeedback) {
        BetterToast.error('Risk review is no longer accepting feedback.')
        await Promise.resolve(refetchTask())
        onClose()
        return
      }
      if (withFeedback && hasPendingUploads) {
        BetterToast.error('Wait for attachments to finish uploading before submitting.')
        return
      }
      isSubmittingRef.current = true
      setIsSubmitting(true)
      try {
        setAttachmentError(null)
        const payload = withFeedback ? buildPayload() : undefined
        const hasContent = Boolean(payload?.feedback) || (payload?.uploadedFiles.length ?? 0) > 0
        await submitRiskReview({ workspaceUuid, uuid, taskId, feedback: payload })
        BetterToast.success(
          hasContent ? 'Risk feedback submitted' : 'Continued without risk feedback'
        )
        await Promise.resolve(refetchTask())
        onClose()
      } catch (error) {
        console.error('Failed to submit risk feedback:', error)
        BetterToast.error('Failed to submit risk feedback')
      } finally {
        isSubmittingRef.current = false
        setIsSubmitting(false)
      }
    },
    [
      canSubmitFeedback,
      hasPendingUploads,
      workspaceUuid,
      uuid,
      taskId,
      refetchTask,
      onClose,
      attachmentIds,
      uploadById,
      feedbackText
    ]
  )

  const isEmptyRiskUi = !migrationDocOutput || groups.length === 0
  const shouldInterceptEmptyRiskEscape = canSubmitFeedback && isEmptyRiskUi

  useEffect(() => {
    if (!shouldInterceptEmptyRiskEscape) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      void submit(false)
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [shouldInterceptEmptyRiskEscape, submit])

  // ─── Empty state ────────────────────────────────────────────────────────

  if (!migrationDocOutput || groups.length === 0) {
    return (
      <div className='flex h-[941px] max-h-[92vh] w-[1059px] max-w-[95vw] items-center justify-center rounded-[28px] bg-tokens-neutral-bg-white shadow-heavy'>
        <div className='text-center'>
          <Typography
            fontFamily='geist'
            fontSize='titleMd'
            fontWeight='semibold'
            className='text-tokens-neutral-text-primary'
          >
            No risks to review.
          </Typography>
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            className='mt-1 text-tokens-neutral-text-tertiary'
          >
            The agent did not flag any risks for this migration.
          </Typography>
          <div className='mt-6'>
            <Button onClick={() => (canSubmitFeedback ? void submit(false) : onClose())}>
              Close
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // ─── Slots ──────────────────────────────────────────────────────────────

  const sidebarHeader = (
    <div className='min-w-0'>
      <Typography
        fontFamily='geist'
        fontSize='titleSm'
        fontWeight='bold'
        className='text-tokens-neutral-text-primary'
      >
        Assumptions & Risks
      </Typography>
    </div>
  )

  const sidebar = (
    <div className='space-y-2 px-4'>
      {groups.map(g => {
        const isActive = activeEndpointRef?.endpointKey === g.endpointKey
        return (
          <button
            key={g.endpointKey}
            type='button'
            data-testid={`risk-endpoint-item-${g.endpointKey}`}
            onClick={() => setActiveEndpointKey(g.endpointKey)}
            className={cm(
              'flex h-12 w-full items-center gap-2 rounded-[10px] px-2.5 py-2 text-left transition-colors',
              isActive
                ? 'bg-tokens-neutral-fill-fill-4 text-tokens-neutral-text-primary'
                : 'bg-transparent text-tokens-neutral-text-secondary hover:bg-tokens-neutral-fill-fill-4 hover:text-tokens-neutral-text-primary'
            )}
          >
            <FileText className='mt-0.5 h-4 w-4 flex-shrink-0 text-tokens-neutral-icon-default' />
            <div className='min-w-0 flex-1'>
              <Typography
                fontFamily='geist'
                fontSize='bodySmMedium'
                fontWeight='medium'
                className='text-tokens-neutral-text-primary truncate'
              >
                {g.endpointKey}
              </Typography>
            </div>
          </button>
        )
      })}
    </div>
  )

  const headerLeft = (
    <div className='min-w-0'>
      <Typography
        fontFamily='geist'
        fontSize='bodyMd'
        fontWeight='medium'
        className='truncate text-tokens-neutral-text-primary'
      >
        Risks in {activeEndpointRef?.endpointKey ?? '—'}
      </Typography>
    </div>
  )

  const headerRight = <CloseButton onClose={onClose} isDisabled={isSubmitting} />

  // The body needs `relative` so the floating chatbox can absolutely
  // position itself; bottom padding reserves space so the article doesn't
  // hide behind the chatbox.
  const content = (
    <div className='relative h-full w-full bg-tokens-neutral-bg-white'>
      <div className='h-full w-full overflow-y-auto px-12 pt-12 pb-[260px]'>
        {activeGroup ? (
          <article className='mx-auto max-w-[722px]'>
            <div className='mb-7 flex items-center gap-2'>
              <IconCurieStar className='h-4 w-4' />
              <Typography
                fontFamily='geist'
                fontSize='bodyMdMedium'
                fontWeight='medium'
                className='text-tokens-primary-2'
              >
                Assumption {activeFlatIndex + 1}
              </Typography>
            </div>

            <RiskFieldMarkdown md={activeGroup.rawMd} />
          </article>
        ) : (
          <div className='flex h-full items-center justify-center'>
            <Typography
              fontFamily='geist'
              fontSize='bodyMd'
              className='text-tokens-neutral-text-tertiary'
            >
              Select a risk from the sidebar.
            </Typography>
          </div>
        )}
      </div>

      {canSubmitFeedback && !isFeedbackOpen ? (
        <ReviewChoiceHub
          onAcknowledge={() => submit(false)}
          onProvideFeedback={() => setIsFeedbackOpen(true)}
          isSubmitting={isSubmitting}
        />
      ) : canSubmitFeedback ? (
        <ChatBox
          feedback={feedbackText}
          onFeedbackChange={setFeedbackText}
          attachments={attachments}
          hasPendingUploads={hasPendingUploads}
          attachmentError={attachmentError}
          onAttachFiles={attachFiles}
          onAttachFolder={attachFolder}
          onRemoveAttachment={removeAttachment}
          onCancel={() => setIsFeedbackOpen(false)}
          onSubmit={() => submit(true)}
          isSubmitting={isSubmitting}
        />
      ) : submittedReviewFeedback ? (
        <ReadOnlyFeedbackPanel
          feedback={submittedReviewFeedback.feedback}
          attachments={submittedReviewFeedback.uploadedFiles}
          workspaceUuid={workspaceUuid}
        />
      ) : null}
    </div>
  )

  return (
    <SplitPaneViewer
      width={1059}
      height={941}
      initialSidebarWidth={241}
      isResizable={false}
      sidebarClassName='bg-tokens-neutral-bg-grey-1'
      contentClassName='bg-tokens-neutral-bg-white'
      sidebarHeader={sidebarHeader}
      sidebar={sidebar}
      headerLeft={headerLeft}
      headerRight={headerRight}
      content={content}
      footer={null}
    />
  )
}
